import { AURORA_INTERACTION_HELPERS, AURORA_INTERACTION_UNIFORMS, } from "./interaction-shader";
import { AURORA_HALFTONE_HELPERS, AURORA_HALFTONE_UNIFORMS, } from "./halftone-shader";
export const VERTEX_SHADER = `#version 300 es
in vec2 aPosition;
out vec2 vTextureCoord;
void main() {
  gl_Position = vec4(aPosition, 0.0, 1.0);
  vTextureCoord = (aPosition + 1.0) * 0.5;
}
`;
const LAYER_PROGRESS = Array.from({ length: 20 }, (_, index) => ((index + 1) / 20).toFixed(10)).join(", ");
const LAYER_MOTION_WEIGHT = Array.from({ length: 20 }, (_, index) => {
    const progress = (index + 1) / 20;
    return (4 * progress * (1 - progress)).toFixed(10);
}).join(", ");
const LAYER_FAN_WEIGHT = Array.from({ length: 20 }, (_, index) => {
    const position = ((index + 1) / 20) * 2 - 1;
    return (Math.sign(position) * Math.abs(position) ** 1.65).toFixed(10);
}).join(", ");
const LAYER_TWIST_DRIFT = Array.from({ length: 20 }, (_, index) => {
    const position = ((index + 1) / 20) * 2 - 1;
    return (0.04 * Math.sin(position * 1.8)).toFixed(10);
}).join(", ");
const shaderDefines = (features) => `
#define AURORA_SHARED_FIRST_OCTAVE ${features.sharedFirstOctave ? 1 : 0}
#define AURORA_WARP_LINEAR ${features.warpLinear ? 1 : 0}
#define AURORA_WARP_SAMPLE ${features.warp === "sample" ? 1 : 0}
#define AURORA_WARP_PREPASS ${features.warp === "prepass" ? 1 : 0}
#define AURORA_INTERACTION ${features.interaction ? 1 : 0}
#define AURORA_SHAPE_LINE ${features.shape === "line" ? 1 : 0}
#define AURORA_SHAPE_TRIANGLE ${features.shape === "triangle" ? 1 : 0}
#define AURORA_CURVE ${features.curve ? 1 : 0}
#define AURORA_FUNNEL ${features.funnel ? 1 : 0}
#define AURORA_OPTIMIZED ${features.optimized ? 1 : 0}
#define AURORA_PALETTE_RECURRENCE ${features.paletteRecurrence ?? features.optimized ? 1 : 0}
`;
const glslFloat = (value) => {
    const literal = Math.fround(value).toPrecision(9);
    return literal.includes(".") || literal.includes("e") ? literal : `${literal}.0`;
};
const layerPaletteRotation = (sweepSpan, fn) => Array.from({ length: 20 }, (_, index) => glslFloat(fn(Math.PI * 2 * sweepSpan * (index % 4) / 20))).join(", ");
const glslVec2 = (value) => `vec2(${glslFloat(value[0])}, ${glslFloat(value[1])})`;
const glslVec3 = (value) => `vec3(${glslFloat(value[0])}, ${glslFloat(value[1])}, ${glslFloat(value[2])})`;
const paletteColorAt = (palette, progress) => {
    const t = palette.sweepStart + progress * palette.sweepSpan;
    return palette.phase.map((phase) => 0.5 + 0.5 * Math.cos(Math.PI * 2 * (t + phase)));
};
const shaderConstantDefines = (constants) => constants
    ? `
#define uPalettePhase (${glslVec3(constants.palette.phase)})
#define uPaletteStart (${glslFloat(constants.palette.sweepStart)})
#define uPaletteSpan (${glslFloat(constants.palette.sweepSpan)})
#define uMixAmount (${glslFloat(constants.palette.mixAmount)})
#define uHalftoneGranularity (${glslFloat(constants.halftone.granularity)})
#define uHalftoneRadius (${glslVec2(constants.halftone.radius)})
#define uHalftoneFoldFadeAnisotropy (${glslVec2(constants.halftone.foldFadeAnisotropy)})
#define uHalftoneLineFloor (${glslFloat(constants.halftone.lineFloor)})
#define uHalftoneDotGain (${glslFloat(constants.halftone.dotGain)})
#define uHalftoneEdgeSoftness (${glslFloat(constants.halftone.edgeSoftness)})
#define uHalftoneBloomGain (${glslFloat(constants.halftone.bloomGain)})
#define uHalftoneSeed (${glslFloat(constants.halftone.seed)})
#define uPointerFeather (${glslFloat(constants.interaction.feather)})
#define uPointerCoreExclusion (${glslFloat(constants.interaction.coreExclusion)})
#define uPointerDotMagnificationGain (${glslFloat(constants.interaction.dotMagnificationGain)})
#define uPointerVelocityBiasPx (${glslFloat(constants.interaction.velocityBiasPx)})
#define uEmissionOccupancy (${glslFloat(constants.interaction.emissionOccupancy)})
#define uEmissionRadiusScale (${glslFloat(constants.interaction.emissionRadiusScale)})
#define uEmissionSoftnessScale (${glslFloat(constants.interaction.emissionSoftnessScale)})
#define uEmissionPeelPx (${glslVec2(constants.interaction.emissionPeelDistancePx)})
#define uEmissionLifetime (${glslVec2(constants.interaction.emissionLifetimeSeconds)})
#define uEmissionOpacity (${glslFloat(constants.interaction.emissionOpacity)})
#define uEmissionSeed (${glslFloat(constants.interaction.emissionSeed)})
const vec3 AURORA_PALETTE_ZERO = ${glslVec3(paletteColorAt(constants.palette, 0))};
const vec3 AURORA_PALETTE_ONE = ${glslVec3(paletteColorAt(constants.palette, 1))};
const float LAYER_PALETTE_ROTATION_COS[20] = float[20](${layerPaletteRotation(constants.palette.sweepSpan, Math.cos)});
const float LAYER_PALETTE_ROTATION_SIN[20] = float[20](${layerPaletteRotation(constants.palette.sweepSpan, Math.sin)});
`
    : "";
// Shared line/triangle aurora engine. Camera framing, palette, ambient motion,
// and interaction behavior are supplied by typed route configurations.
export function createFragmentShader(features, constants) {
    return `#version 300 es
${shaderDefines(features)}
precision highp float;
precision highp int;

in vec2 vTextureCoord;
layout(location = 0) out vec4 fragColor;

uniform vec2 uResolution;
uniform float uCrossAxisScale;
uniform float uSceneHeightRatio;
uniform float uFramingAspect;
uniform float uTime;
uniform vec3 uBgColor;
uniform float uZoom;
uniform vec2 uCenter;
uniform float uLineExtent;
uniform float uRotation;
uniform vec3 uPalettePhase;
uniform float uPaletteStart;
uniform float uPaletteSpan;
uniform float uSpatialColor;
uniform float uMixAmount;
uniform float uSpacing;
uniform float uDither;
uniform float uAmplitude;
uniform float uSpeed;
uniform float uTwistRetention;
uniform float uFanSpread;
uniform float uTwistSpread;
uniform float uHorizontalStretch;
uniform float uUnravel;
uniform float uFunnelStrength;
uniform float uFunnelWidth;
uniform float uFunnelTaperCeiling;
uniform float uFunnelContourFloor;
uniform float uFunnelTurbulence;
uniform float uFunnelSpacing;
uniform float uFunnelLineDefinition;
uniform float uCurveStrength;
uniform float uCurveDisplacementScale;
uniform float uCurveProgress;
uniform float uCurveViewportSpan;
uniform vec3 uCurveXCheckpoints;
uniform vec4 uCurveBendWindows;
uniform float uCurveStartWidthScale;
uniform float uEffectOpacity;
uniform float uEdgeReveal;
uniform float uCoverageMargin;
${AURORA_HALFTONE_UNIFORMS}
${AURORA_INTERACTION_UNIFORMS}
${shaderConstantDefines(constants)}

const int MAX_AURORA_ITERATIONS = 20;
const float DYNAMIC_BLUR_EXP_SCALE = 2.0 * 1.4426950408889634;
const float TAU = 6.28318530718;
const float FUNNEL_WIDTH_SCALE = 19.0;
const float FUNNEL_REFERENCE_ASPECT = 1.44;
const float EDGE_REVEAL_FEATHER = 0.36;
const float LAYER_PROGRESS[20] = float[20](${LAYER_PROGRESS});
const float LAYER_MOTION_WEIGHT[20] = float[20](${LAYER_MOTION_WEIGHT});
const float LAYER_FAN_WEIGHT[20] = float[20](${LAYER_FAN_WEIGHT});
const float LAYER_TWIST_DRIFT[20] = float[20](${LAYER_TWIST_DRIFT});

uvec2 pcg2d(uvec2 v) {
  v = v * 1664525u + 1013904223u;
  v.x += v.y * v.y * 1664525u + 1013904223u;
  v.y += v.x * v.x * 1664525u + 1013904223u;
  v ^= v >> 16;
  v.x += v.y * v.y * 1664525u + 1013904223u;
  v.y += v.x * v.x * 1664525u + 1013904223u;
  return v;
}
float randFibo(vec2 p) {
  uvec2 v = floatBitsToUint(p);
  v = pcg2d(v);
  uint r = v.x ^ v.y;
  return float(r) / float(0xffffffffu);
}
float deband() {
  return (randFibo(gl_FragCoord.xy) - 0.5) / 255.0;
}
vec3 palette(float progress) {
  float t = uPaletteStart + clamp(progress, 0.0, 1.0) * uPaletteSpan;
  return vec3(0.5) + vec3(0.5) * cos(TAU * (vec3(t) + uPalettePhase));
}
vec3 tonemapReinhard(vec3 x) {
  x *= 4.0;
  return x / (1.0 + x);
}
float hash11(float value) {
  value = fract(value * 0.1031);
  value *= value + 33.33;
  value *= value + value;
  return fract(value);
}
${AURORA_HALFTONE_HELPERS}
float smootherRamp(float edgeStart, float edgeEnd, float value) {
  float t = clamp(
    (value - edgeStart) / max(edgeEnd - edgeStart, 0.0001),
    0.0,
    1.0
  );
  return t * t * t * (t * (t * 6.0 - 15.0) + 10.0);
}
float curveCenterX(float progress) {
  float openingBend = smootherRamp(
    uCurveBendWindows.x,
    uCurveBendWindows.y,
    progress
  );
  float returningBend = smootherRamp(
    uCurveBendWindows.z,
    uCurveBendWindows.w,
    progress
  );
  return
    uCurveXCheckpoints.x +
    (uCurveXCheckpoints.y - uCurveXCheckpoints.x) * openingBend +
    (uCurveXCheckpoints.z - uCurveXCheckpoints.y) * returningBend;
}
float curveWidthScale(float progress) {
  float openingProgress = smootherRamp(
    uCurveBendWindows.x,
    uCurveBendWindows.y,
    progress
  );
  float authoredScale = mix(uCurveStartWidthScale, 1.0, openingProgress);
  return mix(1.0, authoredScale, uCurveStrength);
}
float funnelTopWeight(float normalizedY) {
  // Hold the opening above the authored ceiling, then resolve through the
  // original lower tenth into the timeline strip.
  return pow(smootherRamp(0.1, uFunnelTaperCeiling, normalizedY), 0.55);
}
float sdLine(vec2 p, float extent) {
  float halfLen = extent * 2.0;
  vec2 a = vec2(-halfLen, 0.0);
  vec2 b = vec2(halfLen, 0.0);
  vec2 pa = p - a;
  vec2 ba = b - a;
  float h = clamp(dot(pa, ba) / dot(ba, ba), 0.0, 1.0);
  return length(pa - ba * h);
}
float sdEquilateralTriangle(vec2 p, float radius) {
  const float k = 1.73205080757;
  p.x = abs(p.x) - radius;
  p.y += radius / k;
  if (p.x + k * p.y > 0.0) {
    p = vec2(p.x - k * p.y, -k * p.x - p.y) * 0.5;
  }
  p.x -= clamp(p.x, -2.0 * radius, 0.0);
  return -length(p) * sign(p.y);
}
float sceneShapeDistance(vec2 p) {
#if AURORA_SHAPE_LINE
  return sdLine(p, uLineExtent);
#elif AURORA_SHAPE_TRIANGLE
  return abs(sdEquilateralTriangle(p, uLineExtent));
#else
  return uShapeType < 0.5
    ? sdLine(p, uLineExtent)
    : abs(sdEquilateralTriangle(p, uLineExtent));
#endif
}
vec2 sceneShapeNormal(vec2 p) {
#if AURORA_SHAPE_LINE
  float halfLen = uLineExtent * 2.0;
  vec2 a = vec2(-halfLen, 0.0);
  vec2 b = vec2(halfLen, 0.0);
  vec2 ba = b - a;
  float h = clamp(dot(p - a, ba) / dot(ba, ba), 0.0, 1.0);
  vec2 delta = p - (a + ba * h);
  float magnitude = length(delta);
  return magnitude > 0.00001 ? delta / magnitude : vec2(0.0, 1.0);
#else
  const float epsilon = 0.0015;
  vec2 gradient = vec2(
    sceneShapeDistance(p + vec2(epsilon, 0.0)) -
      sceneShapeDistance(p - vec2(epsilon, 0.0)),
    sceneShapeDistance(p + vec2(0.0, epsilon)) -
      sceneShapeDistance(p - vec2(0.0, epsilon))
  );
  float magnitude = length(gradient);
  return magnitude > 0.00001 ? gradient / magnitude : vec2(0.0, 1.0);
#endif
}
${AURORA_INTERACTION_HELPERS}
vec2 turbulence(vec2 pos, float offset, float baseFrequency, float amplitudeStart, float phaseTime) {
  mat2 rotationStep = mat2(0.6, -0.8, 0.8, 0.6);
  float frequency = baseFrequency;
  float amplitude = amplitudeStart;
  for (float i = 0.0; i < 4.0; i++) {
    vec2 wave = sin(frequency * (pos * rotationStep) + i * phaseTime + offset);
    pos += amplitude * rotationStep[0] * wave / frequency;
    rotationStep *= mat2(0.6, -0.8, 0.8, 0.6);
    frequency *= 1.4;
  }
  return pos;
}
struct TurbulencePair {
  vec2 staticWarp;
  vec2 animatedWarp;
};
TurbulencePair turbulencePair(
  vec2 pos,
  float offset,
  float baseFrequency,
  float amplitudeStart,
  float animatedPhaseTime
) {
  mat2 rotationStep = mat2(0.6, -0.8, 0.8, 0.6);
  float frequency = baseFrequency;
  float amplitude = amplitudeStart;
  vec2 staticPos = pos;
  vec2 animatedPos = pos;
#if AURORA_SHARED_FIRST_OCTAVE
  // Octave zero has no time offset: both paths have identical inputs. Share
  // the wave and displacement, retaining all four octaves and their ordering.
  vec2 firstWave = sin(frequency * (pos * rotationStep) + offset);
  staticPos += amplitude * rotationStep[0] * firstWave / frequency;
  animatedPos = staticPos;
  rotationStep *= mat2(0.6, -0.8, 0.8, 0.6);
  frequency *= 1.4;
  for (float i = 1.0; i < 4.0; i++) {
#else
  for (float i = 0.0; i < 4.0; i++) {
#endif
    vec2 staticWave = sin(frequency * (staticPos * rotationStep) + offset);
    vec2 animatedWave = sin(
      frequency * (animatedPos * rotationStep) +
      i * animatedPhaseTime + offset
    );
    staticPos += amplitude * rotationStep[0] * staticWave / frequency;
    animatedPos += amplitude * rotationStep[0] * animatedWave / frequency;
    rotationStep *= mat2(0.6, -0.8, 0.8, 0.6);
    frequency *= 1.4;
  }
  return TurbulencePair(staticPos, animatedPos);
}
#if AURORA_WARP_SAMPLE
uniform highp sampler2DArray uWarpField;
vec4 sampleWarp(int layer) {
  ivec2 size = textureSize(uWarpField, 0).xy;
  vec2 p = vTextureCoord * vec2(size - 2) + 0.5;
#if AURORA_WARP_LINEAR
  return texture(uWarpField, vec3((p + 0.5) / vec2(size), float(layer)));
#else
  ivec2 cell = ivec2(floor(p));
  vec2 f = fract(p);
  return mix(
    mix(texelFetch(uWarpField, ivec3(cell, layer), 0), texelFetch(uWarpField, ivec3(cell + ivec2(1, 0), layer), 0), f.x),
    mix(texelFetch(uWarpField, ivec3(cell + ivec2(0, 1), layer), 0), texelFetch(uWarpField, ivec3(cell + 1, layer), 0), f.x),
    f.y
  );
#endif
}
TurbulencePair sampledPair(int layer) {
  vec4 value = sampleWarp(layer);
  return TurbulencePair(value.xy, value.zw);
}
#endif
#if AURORA_WARP_PREPASS
layout(location = 1) out vec4 warp1;
layout(location = 2) out vec4 warp2;
layout(location = 3) out vec4 warp3;
uniform int uWarpLayer;
uniform vec2 uWarpSize;
#endif
void main() {
#if AURORA_WARP_PREPASS
  vec2 uv = (gl_FragCoord.xy - 1.0) / (uWarpSize - 2.0);
#else
  vec2 uv = vTextureCoord;
#endif
  vec4 bg = vec4(uBgColor, 1.0);
  float edgeDistance = abs(uv.x - 0.5) * 2.0;
  float edgeRevealThreshold = mix(
    1.0 + EDGE_REVEAL_FEATHER,
    -EDGE_REVEAL_FEATHER,
    clamp(uEdgeReveal, 0.0, 1.0)
  );
  float edgeRevealMask = smoothstep(
    edgeRevealThreshold - EDGE_REVEAL_FEATHER,
    edgeRevealThreshold,
    edgeDistance
  );
  float effectVisibility =
    clamp(uEffectOpacity, 0.0, 1.0) * edgeRevealMask;
#if AURORA_OPTIMIZED && !AURORA_WARP_PREPASS
  if (effectVisibility <= 0.0) {
    fragColor = bg;
    return;
  }
#endif
  vec3 auroraLines = vec3(0.0);
  vec3 bloom = vec3(0.0);
#if AURORA_INTERACTION
  vec3 emissionColor = vec3(0.0);
  float emissionMask = 0.0;
#endif
  float auroraMask = 0.0;
  float bloomMask = 0.0;
  float time = uTime * 0.5 * uSpeed;
  // Top-anchor an authored vertical frame. The framebuffer still fills only
  // the real viewport; geometry uses a fixed height so resizing crops twists.
  vec2 sceneUv = vec2(uv.x, 1.0 - (1.0 - uv.y) * uSceneHeightRatio);
  vec2 aspect = vec2(uResolution.x / uResolution.y * uSceneHeightRatio, 1.0);
  float sceneCrossAxisScale = uCrossAxisScale / uSceneHeightRatio;
  vec2 viewPos = (sceneUv - uCenter) * aspect;
  float localRotation = uRotation;
  mat2 rotMatrix = mat2(
    cos(localRotation),
    -sin(localRotation),
    sin(localRotation),
    cos(localRotation)
  );
  vec2 straightLocalViewPos = rotMatrix * viewPos;
#if AURORA_CURVE
  float pathProgress =
    uCurveProgress + straightLocalViewPos.x * uCurveViewportSpan;
  float curveCrossOffset =
    (curveCenterX(pathProgress) - uCenter.x) * aspect.x *
    uCurveStrength * uCurveDisplacementScale;
  // Straighten the authored centerline before evaluating the existing line
  // field. Every downstream warp and material sample shares this curved
  // coordinate frame, so the strand bends without changing its material.
  vec2 localViewPos = vec2(
    straightLocalViewPos.x,
    (straightLocalViewPos.y - curveCrossOffset) * sceneCrossAxisScale
  );
  float curveCrossScale = curveWidthScale(pathProgress);
#else
  vec2 localViewPos = vec2(
    straightLocalViewPos.x,
    straightLocalViewPos.y * sceneCrossAxisScale
  );
  float curveCrossScale = 1.0;
#endif
  vec2 pos = vec2(
    localViewPos.x,
    localViewPos.y / max(curveCrossScale, 0.001)
  ) / max(uZoom, 0.001);
#if AURORA_FUNNEL
  float funnelProfile = uFunnelStrength * funnelTopWeight(sceneUv.y);
  float funnelVisualProfile = max(funnelProfile, uFunnelContourFloor);
  float localAmplitude = mix(uAmplitude, uFunnelTurbulence, funnelProfile);
  float localSpacing = mix(uSpacing, uFunnelSpacing, funnelProfile);
  // Stretch the shared contour, not its individual layers. The width value is
  // the desired viewport-width character of the opening; the coefficient is
  // calibrated against Landing's hero field at its authored camera distance.
  // Reference-lock framing changes only the ribbon's cross axis. Use the
  // active authored aspect here so viewport width cannot re-expand or narrow
  // the funnel after its material scale has been locked.
  float funnelAspectCap = mix(
    1.2,
    2.5,
    smootherRamp(0.55, 0.9, uFramingAspect)
  );
  float funnelAspectCorrection = min(
    pow(FUNNEL_REFERENCE_ASPECT / max(uFramingAspect, 0.4), 3.0),
    funnelAspectCap
  );
  float funnelContourScale =
    mix(
      1.0,
      1.0 + uFunnelWidth * FUNNEL_WIDTH_SCALE * funnelAspectCorrection,
      funnelVisualProfile
    );
#else
  float funnelProfile = 0.0;
  float funnelVisualProfile = 0.0;
  float localAmplitude = uAmplitude;
  float localSpacing = uSpacing;
  float funnelAspectCorrection = 1.0;
  float funnelContourScale = 1.0;
#endif
#if AURORA_WARP_PREPASS
  TurbulencePair field = turbulencePair(pos, LAYER_PROGRESS[uWarpLayer] * localSpacing, 2.0, localAmplitude, time * 0.1);
  fragColor = vec4(field.staticWarp, field.animatedWarp);
  field = turbulencePair(pos, LAYER_PROGRESS[uWarpLayer + 1] * localSpacing, 2.0, localAmplitude, time * 0.1);
  warp1 = vec4(field.staticWarp, field.animatedWarp);
  field = turbulencePair(pos, LAYER_PROGRESS[uWarpLayer + 2] * localSpacing, 2.0, localAmplitude, time * 0.1);
  warp2 = vec4(field.staticWarp, field.animatedWarp);
  field = turbulencePair(pos, LAYER_PROGRESS[uWarpLayer + 3] * localSpacing, 2.0, localAmplitude, time * 0.1);
  warp3 = vec4(field.staticWarp, field.animatedWarp);
#else
#if AURORA_OPTIMIZED && !AURORA_INTERACTION
  // Broad states need a spatial bound: funnelContourScale varies per row.
  // Each octave displaces by at most amplitude/frequency (rotation is unitary).
  // All layers share the same motion, so pairwise blur is bounded by twice
  // this displacement. The 2-unit tail budget bounds omitted bloom within image tolerance.
  bool broadOutside = false;
  if (uCoverageMargin < 0.0) {
    float warpBound = abs(localAmplitude) * 1.2944606414;
    float contourBound = max(1.0, uHorizontalStretch);
    float pairDeltaBound = 2.0 * warpBound * max(1.0, 1.0 / funnelContourScale);
    float blurBound = 0.001 + (exp(2.0 * pairDeltaBound) - 1.0) * 0.78;
    float crossBound =
      (max(2.0, blurBound) + 0.21 * abs(localAmplitude) +
        1.01 * abs(uFanSpread) + 1.04 * abs(uTwistSpread)) * contourBound * funnelContourScale +
      (1.1 + 1.1 * abs(funnelVisualProfile)) * warpBound;
    broadOutside = AURORA_SHAPE_LINE == 1 &&
      uTwistRetention >= 0.0 && uTwistRetention <= 1.0 &&
      funnelVisualProfile >= 0.0 && funnelVisualProfile <= 1.0 && uUnravel >= 0.0 && uUnravel <= 1.0 &&
      funnelContourScale >= 1.0 && uHorizontalStretch >= 1.0 && abs(pos.y) > crossBound;
  }
  if ((uCoverageMargin > 0.0 && sceneShapeDistance(pos) > uCoverageMargin) || broadOutside) {
    // Reproduce the full path's zero-coverage output exactly: a fragment the
    // material never reaches still carries the tonemapped deband noise, mixed
    // by the palette amount and the effect visibility, before the grain.
    vec3 farColor = tonemapReinhard(vec3(deband()));
    vec3 farBackground = mix(bg.rgb, farColor + bg.rgb, uMixAmount);
    farBackground = mix(bg.rgb, farBackground, effectVisibility);
    float backgroundGrain =
      (randFibo(gl_FragCoord.xy + floor(uTime)) - 0.5) / 255.0;
    fragColor = vec4(
      farBackground + backgroundGrain * uDither * effectVisibility,
      1.0
    );
    return;
  }
#endif
#if AURORA_INTERACTION
  vec2 fragmentCss = uv * uCssResolution;
  float pointerDistancePx = length(fragmentCss - uPointerPx);
  vec2 pointerUv = uPointerPx / max(uCssResolution, vec2(1.0));
  pointerUv.y = 1.0 - (1.0 - pointerUv.y) * uSceneHeightRatio;
#if AURORA_FUNNEL
  float pointerFunnelProfile =
    uFunnelStrength * funnelTopWeight(pointerUv.y);
  float pointerContourProfile = max(
    pointerFunnelProfile,
    uFunnelContourFloor
  );
  float pointerSpatialStrength = mix(
    uPointerStrengthScale,
    uPointerFunnelStrengthScale,
    pointerContourProfile
  );
#else
  float pointerFunnelProfile = 0.0;
  float pointerContourProfile = 0.0;
  float pointerSpatialStrength = uPointerStrengthScale;
#endif
  float pointerField = uPointerStrength * pointerSpatialStrength * (
    1.0 - smootherRamp(
      uPointerRadiusPx * (1.0 - uPointerFeather),
      uPointerRadiusPx,
      pointerDistancePx
    )
  );
  float lensMagnification = pointerLensMagnification(pointerField);
  float dotMagnification = 1.0 +
    (lensMagnification - 1.0) * uPointerDotMagnificationGain;
  float halftoneAnnulus = smoothstep(
    uPointerRadiusPx * uPointerCoreExclusion,
    uPointerRadiusPx * (uPointerCoreExclusion + 0.08),
    pointerDistancePx
  );
  vec2 pointerViewPos = (pointerUv - uCenter) * aspect;
  float pointerLocalRotation = uRotation;
  mat2 pointerRotMatrix = mat2(
    cos(pointerLocalRotation),
    -sin(pointerLocalRotation),
    sin(pointerLocalRotation),
    cos(pointerLocalRotation)
  );
  vec2 pointerStraightLocal = pointerRotMatrix * pointerViewPos;
#if AURORA_CURVE
  float pointerPathProgress =
    uCurveProgress + pointerStraightLocal.x * uCurveViewportSpan;
  float pointerCurveOffset =
    (curveCenterX(pointerPathProgress) - uCenter.x) * aspect.x *
    uCurveStrength * uCurveDisplacementScale;
  float pointerCurveCrossScale = curveWidthScale(pointerPathProgress);
#else
  float pointerCurveOffset = 0.0;
  float pointerCurveCrossScale = 1.0;
#endif
  vec2 pointerMaterialPos = vec2(
    pointerStraightLocal.x,
    (pointerStraightLocal.y - pointerCurveOffset) * sceneCrossAxisScale /
      max(pointerCurveCrossScale, 0.001)
  ) / max(uZoom, 0.001);
#if AURORA_FUNNEL
  float pointerContourScale =
    mix(
      1.0,
      1.0 + uFunnelWidth * FUNNEL_WIDTH_SCALE * funnelAspectCorrection,
      pointerContourProfile
    );
#else
  float pointerContourScale = 1.0;
#endif
  pointerMaterialPos.y /= pointerContourScale;
  vec2 pointerVelocityLocal = pointerRotMatrix * vec2(
    uPointerVelocity.x * aspect.x,
    uPointerVelocity.y * uSceneHeightRatio
  );
  pointerVelocityLocal.y *= sceneCrossAxisScale;
  pointerVelocityLocal.y /= max(pointerCurveCrossScale, 0.001);
  pointerVelocityLocal.y /= pointerContourScale;
#else
  float pointerField = 0.0;
  float dotMagnification = 1.0;
  vec2 pointerMaterialPos = vec2(0.0);
  vec2 pointerVelocityLocal = vec2(0.0);
#endif

  float blurMultiplier = 0.05;
  float activeIterationsF = float(MAX_AURORA_ITERATIONS);
  float turbulenceBaseFrequency = 2.0;
  float turbulencePhaseTime = time * 0.1;
#if AURORA_WARP_SAMPLE
  TurbulencePair sharedWarpPair = sampledPair(9);
#else
  TurbulencePair sharedWarpPair = turbulencePair(
    pos,
    0.5 * localSpacing,
    turbulenceBaseFrequency,
    localAmplitude,
    turbulencePhaseTime
  );
#endif
  vec2 staticSharedWarp = sharedWarpPair.staticWarp;
  vec2 animatedSharedWarp = sharedWarpPair.animatedWarp;
  // Move every layer through the same animated centerline deformation. Keeping
  // the per-layer offsets on a fixed phase prevents the ribbon envelope from
  // alternately collapsing and expanding as the idle clock advances.
  vec2 sharedMotionDelta = animatedSharedWarp - staticSharedWarp;
  vec2 sharedMotion = vec2(sharedMotionDelta.x, sharedMotionDelta.y * 0.05);
  vec2 prevTwistedPos = turbulence(
    pos,
    -1.0 / activeIterationsF,
    turbulenceBaseFrequency,
    localAmplitude,
    0.0
  );
  vec2 prevMaterialWarp =
    mix(staticSharedWarp, prevTwistedPos, uTwistRetention) + sharedMotion;
  float funnelCenterlineOffset =
    (staticSharedWarp.y + sharedMotion.y - pos.y) * funnelProfile;
  prevMaterialWarp.y -= funnelCenterlineOffset;
  prevMaterialWarp.y /= funnelContourScale;
#if AURORA_INTERACTION
  vec2 prevBaseWarp = pointerDeform(
    prevMaterialWarp,
    pointerMaterialPos,
    pointerVelocityLocal,
    pointerField
  );
#else
  vec2 prevBaseWarp = prevMaterialWarp;
#endif
  float wrapperEnvelope = mix(0.72, 1.0, smoothstep(0.04, 0.42, abs(localViewPos.x)));
  float wrapperDrift = 1.0 + 0.01 * sin(localViewPos.x * 9.0);
  float contourStretch = mix(1.0, uHorizontalStretch, wrapperEnvelope);
  float twistSweep = sin(localViewPos.x * 3.4);
  // Layer ordering reverses whenever the authored twist crosses through zero.
  // Resolve that ordering once at the pointer so the entire radial field uses
  // one stable outward direction. Without this normalization a fixed inverse-
  // map offset widens one twist phase but cancels the opposite phase, which
  // presents as a pinch on one side of the same ribbon.
#if AURORA_INTERACTION
  float pointerTwistSweep = sin(pointerStraightLocal.x * 3.4);
  float pointerAuthoredSpread =
    uFanSpread + uTwistSpread * pointerTwistSweep;
  float pointerSpreadDirection =
    pointerAuthoredSpread > 0.0001 ? 1.0 : -1.0;
#endif
  // Keep the spatial funnel centered and nearly vertical. Other visual states
  // retain the shared traveling wave, while the open hero damps it almost
  // completely so idle motion reads as material flow rather than a swaying
  // centerline.
  float funnelMotionDamping = mix(1.0, 0.08, funnelProfile);
  float centerlineWave = localAmplitude * funnelMotionDamping * (
    0.16 * sin(localViewPos.x * 3.2 - time) +
    0.05 * sin(localViewPos.x * 7.1 + time * 0.45)
  );
  float funnelDotScale = mix(
    1.0,
    1.0 + max(uFunnelLineDefinition - 1.0, 0.0) * 6.0,
    funnelProfile
  );
  float smoothing = mix(0.53, 0.78, uUnravel);
  float funnelDefinitionProgress = clamp(
    (uFunnelLineDefinition - 1.0) * funnelProfile,
    0.0,
    1.0
  );
  float halftoneCrossCorrection = mix(
    1.0,
    funnelContourScale,
    funnelDefinitionProgress
  );
  float definedLineFloor = mix(
    uHalftoneLineFloor,
    uHalftoneLineFloor * 0.15,
    funnelDefinitionProgress
  );
  float funnelLineScale = mix(1.0, uFunnelLineDefinition, funnelProfile);
  float funnelBloomScale = mix(
    1.0,
    1.0 / max(uFunnelLineDefinition * uFunnelLineDefinition, 1.0),
    funnelProfile
  );
  float halftoneGranularity = auroraHalftoneGranularity();
#if AURORA_PALETTE_RECURRENCE
  float spatialPaletteShift =
    (uv.x - 0.5) * uSpatialColor + (uv.y - 0.5) * uSpatialColor;
  vec3 paletteBaseCos = vec3(1.0);
  vec3 paletteBaseSin = vec3(0.0);
#endif
#if AURORA_INTERACTION
  float interactionSpread =
    uPointerSpreadPx * uCrossAxisScale /
    max(uCssResolution.y * uZoom * curveCrossScale, 1.0);
#endif

  for (int i = 0; i < MAX_AURORA_ITERATIONS; i++) {
    float iterationProgress = LAYER_PROGRESS[i];
    TurbulencePair twistedPair;
#if AURORA_WARP_SAMPLE
    twistedPair = sampledPair(i);
#elif AURORA_OPTIMIZED
    // Layer 9's progress is exactly 0.5, so its warp pair is the shared warp
    // already evaluated above — the same call with the same arguments.
    if (i == 9) {
      twistedPair = sharedWarpPair;
    } else {
      twistedPair = turbulencePair(
        pos,
        iterationProgress * localSpacing,
        turbulenceBaseFrequency,
        localAmplitude,
        turbulencePhaseTime
      );
    }
#else
    twistedPair = turbulencePair(
      pos,
      iterationProgress * localSpacing,
      turbulenceBaseFrequency,
      localAmplitude,
      turbulencePhaseTime
    );
#endif
    vec2 staticTwistedPos = twistedPair.staticWarp;
    vec2 animatedTwistedPos = twistedPair.animatedWarp;
    // Anchor the outer bands and return animated turbulence to the interior.
    // This restores the colored twist without allowing its silhouette to fold
    // down to the narrow phase that prompted the stability correction.
    float funnelLayerMotionDamping = mix(1.0, 0.08, funnelProfile);
    vec2 twistedPos = mix(
      staticTwistedPos,
      animatedTwistedPos,
      LAYER_MOTION_WEIGHT[i] * 0.4 * funnelLayerMotionDamping
    );
    vec2 materialWarp =
      mix(staticSharedWarp, twistedPos, uTwistRetention) + sharedMotion;
    // Retain Landing's relative inter-layer turbulence, but remove the shared
    // lateral displacement that would otherwise bend the entire funnel into a
    // large S-curve.
    materialWarp.y -= funnelCenterlineOffset;
    materialWarp.y /= funnelContourScale;
#if AURORA_INTERACTION
    vec2 baseWarp = pointerDeform(
      materialWarp,
      pointerMaterialPos,
      pointerVelocityLocal,
      pointerField
    );
#else
    vec2 baseWarp = materialWarp;
#endif
    vec2 warpedPos = baseWarp;
    float fanWeight = LAYER_FAN_WEIGHT[i];
    warpedPos.y /= contourStretch;
    warpedPos.y += centerlineWave;
    warpedPos.y += fanWeight * uFanSpread * wrapperEnvelope * wrapperDrift;
    // Locally fan the authored layers across the ribbon normal. This is
    // independent from the radial push so the hovered band opens visibly
    // instead of moving as one solid sheet.
#if AURORA_INTERACTION
    // This shader maps output fragments back into ribbon material. Increase
    // the magnitude of the pointer-local authored spread regardless of which
    // layer ordering the twist currently uses. Reuse the nonlinear fan weight
    // so outer colors open strongly while center layers remain continuous.
    warpedPos.y +=
      fanWeight * pointerSpreadDirection * interactionSpread * pointerField;
#endif
    // A low-frequency crossing sweep keeps the opened wash reading as one
    // ribbon while its outer colors peel toward opposite viewport edges. The
    // center stays nearly aligned; the upper and lower reaches twist apart.
    warpedPos.y +=
      fanWeight * uTwistSpread *
      (twistSweep + LAYER_TWIST_DRIFT[i]) * wrapperEnvelope;
    float shapeDistance = sceneShapeDistance(warpedPos);
    // Fan separation is deliberate geometry, not inter-layer motion blur. Keep
    // the original turbulence-based blur so unfurled highlights stay legible.
    float positionDelta = distance(baseWarp, prevBaseWarp);
    prevBaseWarp = baseWarp;
    float dynamicBlur = exp2(positionDelta * DYNAMIC_BLUR_EXP_SCALE) - 1.0;
    float blurWidth = 0.02 * blurMultiplier + max(dynamicBlur * smoothing, 0.001);
    float lineStrength = 1.0 - smoothstep(0.0, blurWidth, shapeDistance);
    // Derivatives are evaluated unconditionally for every layer. Reusing this
    // basis for hover emission avoids undefined derivative behavior inside the
    // pointer-only branch and limits the roundness correction to one Jacobian.
    // The contour's deliberate mobile widening is not a material fold. Restore
    // that known cross-axis scale for halftone sampling so the anisotropy guard
    // does not fade every dot in the broad opening.
    vec2 halftoneMaterialPos = warpedPos;
    halftoneMaterialPos.y *= curveCrossScale;
    halftoneMaterialPos.y *= halftoneCrossCorrection;
    vec2 materialGrid = halftoneMaterialPos * halftoneGranularity;
    vec2 materialGridDerivativeX = dFdx(materialGrid);
    vec2 materialGridDerivativeY = dFdy(materialGrid);
    float materialCellHash = 0.0;
    vec2 materialCellCenter = vec2(0.0);
    float materialDot = auroraHalftoneDot(
      halftoneMaterialPos,
      materialGridDerivativeX,
      materialGridDerivativeY,
      halftoneGranularity,
      iterationProgress,
      dotMagnification * funnelDotScale,
      dotMagnification,
      materialCellHash,
      materialCellCenter
    );
    lineStrength *= materialDot * uHalftoneDotGain + definedLineFloor;
    lineStrength *= funnelLineScale;
#if AURORA_PALETTE_RECURRENCE
    float paletteRawProgress = iterationProgress + spatialPaletteShift;
    if (i == 0 || i == 4 || i == 8 || i == 12 || i == 16) {
      vec3 paletteAngle = TAU * (
        vec3(uPaletteStart + paletteRawProgress * uPaletteSpan) + uPalettePhase
      );
      paletteBaseCos = cos(paletteAngle);
      paletteBaseSin = sin(paletteAngle);
    }
    vec3 lineColor = paletteRawProgress <= 0.0
      ? AURORA_PALETTE_ZERO
      : paletteRawProgress >= 1.0
        ? AURORA_PALETTE_ONE
        : vec3(0.5) + vec3(0.5) * (
          paletteBaseCos * LAYER_PALETTE_ROTATION_COS[i] -
          paletteBaseSin * LAYER_PALETTE_ROTATION_SIN[i]
        );
#else
    float spatialPaletteShift =
      (uv.x - 0.5) * uSpatialColor + (uv.y - 0.5) * uSpatialColor;
    vec3 lineColor = palette(iterationProgress + spatialPaletteShift);
#endif
    float inverseDistance = 1.0 / max(shapeDistance + dynamicBlur, 0.001);
    float bloomStrength =
      clamp(inverseDistance, 0.0, 250.0) * materialDot * uHalftoneBloomGain;
    bloomStrength *= funnelBloomScale;
    auroraLines -= lineStrength * lineColor;
    auroraMask += lineStrength;
    bloom += bloomStrength * lineColor;
    bloomMask += bloomStrength;

#if AURORA_INTERACTION
    if (pointerField > 0.001 && halftoneAnnulus > 0.001) {
      float emitted = evaluateAuroraEmission(
        warpedPos,
        materialGridDerivativeX,
        materialGridDerivativeY,
        sceneShapeNormal(warpedPos),
        halftoneGranularity,
        iterationProgress,
        pointerVelocityLocal,
        pointerField,
        dotMagnification,
        halftoneAnnulus
      );
      emissionColor += emitted * lineColor;
      emissionMask += emitted;
    }
#endif

  }

  auroraLines *= 1.0 / activeIterationsF * 2.0;
  auroraMask *= 1.0 / activeIterationsF * 2.0;
  float bloomSoftness = mix(2e4, 1.2e4, uUnravel);
  float bloomContribution = mix(0.66, 0.78, uUnravel);
  bloom = bloom / (bloom + bloomSoftness);
  bloomMask = bloomMask / (bloomMask + bloomSoftness);
  vec3 color = -auroraLines + bloom * 3.0 * bloomContribution;
  float accumulation = (auroraMask + bloomMask * 3.0 * bloomContribution) * 1.2;
  color *= 1.2;
  color += deband();
  color = tonemapReinhard(color);
  float effectAlpha = clamp(tonemapReinhard(vec3(accumulation)).r, 0.0, 1.0);
  vec3 blended = color + bg.rgb * (1.0 - effectAlpha);

  // At close camera distances, many translucent line layers naturally average
  // toward cyan. Reintroduce a restrained, slowly drifting palette spread inside
  // the aurora mask so the magnified line still reads as broad color bands.
  // Bend that tint in opposite directions across the centerline so the color
  // overlap follows the geometric twist without introducing another hard shape.
  float twistTintShift =
    localViewPos.y * sin(localViewPos.x * 3.4) * uTwistSpread * 4.8;
  float spatialTintOffset =
    (uv.x - 0.5) * uSpatialColor + (uv.y - 0.5) * uSpatialColor +
    twistTintShift + uTime * uSpeed * 0.008;
  vec3 spatialTint = palette(0.5 + spatialTintOffset);
  float spatialTintMix =
    clamp(uSpatialColor * 0.8 + uTwistSpread * 2.2, 0.0, 0.4) * effectAlpha;
  blended = mix(blended, spatialTint, spatialTintMix);

  // Match the Palette Lab recipe by compositing every palette contribution
  // over white only after the aurora's internal tinting is complete.
  vec3 outColor = mix(bg.rgb, blended, uMixAmount);

#if AURORA_INTERACTION
  if (emissionMask > 0.0001) {
    vec3 emittedColor = emissionColor / emissionMask;
    float emittedAlpha = clamp(emissionMask * 0.62, 0.0, 0.48);
    // The widened analytic edge supplies a soft halo without a feedback pass.
    outColor = mix(outColor, mix(emittedColor, vec3(1.0), 0.2), emittedAlpha);
  }
#endif
  outColor = mix(bg.rgb, outColor, effectVisibility);
  float grain = (randFibo(gl_FragCoord.xy + floor(uTime)) - 0.5) / 255.0;
  outColor += grain * uDither * effectVisibility;
  fragColor = vec4(outColor, 1.0);
#endif
}
`;
}
export const FRAGMENT_SHADER = createFragmentShader({
    interaction: true,
    shape: "dynamic",
    curve: true,
    funnel: true,
    optimized: false,
});
//# sourceMappingURL=base-shader.js.map